import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, Crown, ArrowRight } from "lucide-react";
import { fetchMySubscription, verifyProSubscription } from "@/lib/api";

/**
 * Return URL after CinetPay checkout (or dryrun mode).
 * Polls the backend subscription endpoint up to 6 times (every 2 s) since
 * webhook activation may happen a few seconds after the redirect.
 */
export default function AbonnementsProRetourPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("loading"); // loading | success | pending | failed
  const [sub, setSub] = useState(null);
  const tx = params.get("tx") || params.get("transaction_id") || "";
  const isDryrun = params.get("dryrun") === "1";

  useEffect(() => {
    let attempts = 0;
    let stopped = false;
    const poll = async () => {
      try {
        // Active verification — re-checks the payment server-side instead of
        // waiting passively for the (sometimes unreliable) webhook.
        const v = await verifyProSubscription(tx);
        if (stopped) return;
        const isActive = v?.activated || v?.subscription?.status === "active";
        if (isActive) {
          setSub(v.subscription);
          setStatus("success");
          return;
        }
        if (v?.payment_status === "failed") {
          setStatus("failed");
          return;
        }
      } catch { /* ignore — keep polling */ }
      attempts += 1;
      if (attempts >= 8) {
        setStatus("pending");
        return;
      }
      setTimeout(poll, 2500);
    };
    poll();
    return () => { stopped = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tx]);

  return (
    <main className="min-h-screen flex items-center justify-center px-6" data-testid="pro-retour-page">
      <div className="max-w-md w-full glass-card p-8 text-center">
        {status === "loading" && (
          <>
            <Loader2 className="mx-auto animate-spin text-orange-500 mb-4" size={40} />
            <h1 className="text-2xl font-bold mb-2">Confirmation du paiement…</h1>
            <p className="text-sm text-[var(--ink-soft)]">
              Nous vérifions votre transaction <code className="text-xs">{tx.slice(0, 10)}…</code>
            </p>
          </>
        )}

        {status === "success" && (
          <div data-testid="pro-retour-success">
            <CheckCircle2 className="mx-auto text-emerald-500 mb-4" size={48} />
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-100 text-amber-800 text-xs font-bold uppercase tracking-wider mb-3">
              <Crown size={14} /> Fodindin Pro activé
            </span>
            <h1 className="text-2xl font-bold mb-2">
              Bienvenue dans la Pro League 🎉
            </h1>
            <p className="text-sm text-[var(--ink-soft)] mb-1">
              {isDryrun ? "Mode test (sandbox) : " : ""}
              Votre abonnement est actif jusqu'au {sub?.current_period_end ? new Date(sub.current_period_end).toLocaleDateString("fr-FR") : "—"}.
            </p>
            <p className="text-sm text-[var(--ink-soft)] mb-5">
              Toutes les fonctionnalités IA sont débloquées.
            </p>
            <Link
              to="/deposer"
              className="inline-flex items-center justify-center gap-2 w-full py-3 rounded-2xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold"
              data-testid="pro-retour-cta"
            >
              Déposer mon annonce (jusqu'à 6 photos) <ArrowRight size={16} />
            </Link>
            <Link to="/choisir-abonnement" className="block mt-3 text-sm text-orange-600 font-semibold" data-testid="pro-retour-suivi">
              Suivre mon inscription
            </Link>
          </div>
        )}

        {status === "pending" && (
          <>
            <Loader2 className="mx-auto text-amber-500 mb-4" size={40} />
            <h1 className="text-2xl font-bold mb-2">Paiement en cours…</h1>
            <p className="text-sm text-[var(--ink-soft)] mb-5">
              Cela peut prendre quelques minutes. Vous recevrez une confirmation
              dès que CinetPay valide votre transaction.
            </p>
            <button
              type="button"
              onClick={() => navigate("/mon-compte")}
              className="btn-primary w-full !py-3"
            >
              Aller à mon compte
            </button>
          </>
        )}

        {status === "failed" && (
          <>
            <XCircle className="mx-auto text-red-500 mb-4" size={48} />
            <h1 className="text-2xl font-bold mb-2">Paiement non finalisé</h1>
            <p className="text-sm text-[var(--ink-soft)] mb-5">
              Aucun montant n'a été débité. Vous pouvez réessayer quand vous le souhaitez.
            </p>
            <Link
              to="/abonnements/pro"
              className="btn-primary inline-flex w-full !py-3 items-center justify-center gap-2"
            >
              Réessayer <ArrowRight size={16} />
            </Link>
          </>
        )}
      </div>
    </main>
  );
}
